// Wire types and URL helpers for the conversations-api artifacts endpoints.
// Mirrors `ArtifactPreviewResponse`, `ArtifactDownloadRequest/Result`, and
// `ArtifactExportRequest/Result` in `product/ai/libs/conversations-types`.

import { apiMutate } from "~/utils/apiClient";

const { VITE_CONVERSATIONS_API } = import.meta.env;

// "image" and "excel" aren't stored ArtifactTypes in the backend taxonomy —
// the conversations preview service synthesizes them as render hints for
// image blobs and small .xlsx blobs respectively (see
// product/ai/services/conversations/.../preview_service.py).
export type ArtifactType = "dataframe" | "figure" | "value" | "text" | "blob" | "image" | "excel";

// Output formats accepted by the convert-and-copy Lambda. Not every type
// supports every format; see `formatOptionsFor` in `artifacts/formats.ts`.
export type ConvertAndCopyFormat = "csv" | "parquet" | "zip" | "html" | "pickle" | "original";

export type ConvertAndCopyStatus = "ready" | "processing";

export type ArtifactContent =
  | Record<string, unknown>
  | unknown[]
  | string
  | number
  | boolean
  | null;

export interface ArtifactPreview {
  artifact_id: string;
  artifact_type: ArtifactType;
  artifact_name: string | null;
  filename: string | null;
  summary: string | null;
  code: string | null;
  content: ArtifactContent;
  generated_at: string;
  version: number;
}

export interface ArtifactDownloadRequest {
  format: ConvertAndCopyFormat;
}

export interface ArtifactDownloadResult {
  download_url: string;
  file_name: string;
  content_type: string;
  status: ConvertAndCopyStatus;
}

export interface ArtifactExportRequest {
  project_id: number;
  format: ConvertAndCopyFormat;
}

export interface ArtifactExportResult {
  file_name: string;
  file_path: string;
  file_size_bytes: number;
  s3_key: string;
}

export const artifactPreviewsPath = (conversationId: string, names: string[]) => {
  const params = new URLSearchParams();
  for (const name of names) params.append("names", name);
  return `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/previews?${params.toString()}`;
};

// Share-token-scoped preview lookup. A row in the shares table is the
// authorization: any token holder can resolve artifacts on the shared
// conversation.
export const sharedArtifactPreviewsPath = (token: string, names: string[]) => {
  const params = new URLSearchParams();
  for (const name of names) params.append("names", name);
  return `${VITE_CONVERSATIONS_API}/shares/${token}/artifacts/previews?${params.toString()}`;
};

// Single-artifact preview resolved by id (not name). User-attachment cards key
// on the artifact id linked to the user message, so they resolve this way.
export const artifactPreviewByIdPath = (conversationId: string, artifactId: string) =>
  `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/${artifactId}/preview`;

// Share-token-scoped single-artifact preview by id; the shares-table row is the
// authorization.
export const sharedArtifactPreviewByIdPath = (token: string, artifactId: string) =>
  `${VITE_CONVERSATIONS_API}/shares/${token}/artifacts/${artifactId}/preview`;

export const artifactDownloadPath = (conversationId: string, artifactId: string) =>
  `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/${artifactId}/download`;

export const artifactMetadataPath = (conversationId: string, artifactId: string) =>
  `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/${artifactId}`;

export interface ArtifactMetadata {
  download_url: string;
  custom_metadata?: Record<string, unknown> | null;
}

export const artifactExportPath = (conversationId: string, artifactId: string) =>
  `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/${artifactId}/export`;

// Lineage is currently served by analysis-agent (there's a TODO to move it
// to conversations-api). The response is a directed-graph payload with
// nodes/edges plus a pre-rendered Mermaid string.
export interface ArtifactLineageNode {
  artifact_id: string;
  // The legacy types carry more fields, but the renderer only needs to
  // know "is there anything to draw" — leave the rest opaque.
}

export interface ArtifactLineageEdge {
  from: string;
  to: string;
}

export interface ArtifactLineageGraph {
  nodes: ArtifactLineageNode[];
  edges: ArtifactLineageEdge[];
  mermaid: string;
}

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

export const artifactLineagePath = (conversationId: string, artifactId: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${conversationId}/artifacts/${artifactId}/lineage`;

// ---------------------------------------------------------------------------
// User uploads (attaching files to a conversation)
// ---------------------------------------------------------------------------

// Stored file taxonomy, mirroring `FileType` in conversations-types. Only text
// and blob are reachable from a browser upload; parquet/pickle are produced by
// the agent's own tools.
export type FileType = "parquet" | "pickle" | "text" | "blob";

// Request body for the artifact upload endpoints. Mirrors `ArtifactCreate` in
// conversations-types. `name` is omitted so the server mints a collision-free
// `artifact_<uuid>` handle (the same convention the agent's retrieve_file tool
// uses); the real filename rides in `filename`.
export interface ArtifactCreate {
  filetype: FileType;
  artifact_type: ArtifactType;
  filename?: string;
  summary?: string | null;
  custom_metadata?: Record<string, unknown> | null;
}

// Request body for `POST /{conversationId}/artifacts/upload-url`. Declaring
// `size_bytes` binds the presigned URL to exactly that length, so an oversized
// or tampered body is refused by S3 rather than by us after the fact.
interface ArtifactUploadUrlRequest extends ArtifactCreate {
  size_bytes: number;
}

// Request body for `POST /{conversationId}/artifacts/from-upload`: the creation
// fields plus the S3 location returned by `upload-url`.
interface ArtifactCreateFromUpload extends ArtifactCreate {
  bucket: string;
  key: string;
}

// Response from `POST /{conversationId}/artifacts/upload-url`: a presigned S3
// URL and the object location, with no artifact record created yet.
interface PresignedUpload {
  upload_url: string;
  bucket: string;
  key: string;
}

// Extensions the agent's sandbox loads as text. Kept in sync with
// `_TEXT_EXTENSIONS` in
// product/ai/agents/analysis-agent/.../tools/data_retrieval_tools.py — the
// Python side is the source of truth; anything not listed here loads as a blob.
const TEXT_EXTENSIONS = new Set([
  ".md",
  ".txt",
  ".rst",
  ".tex",
  ".bib",
  ".json",
  ".jsonl",
  ".ndjson",
  ".yaml",
  ".yml",
  ".xml",
  ".csv",
  ".tsv",
  ".toml",
  ".ini",
  ".cfg",
  ".conf",
  ".properties",
  ".html",
  ".css",
  ".js",
  ".py",
  ".r",
  ".sql",
  ".sh",
  ".sas",
  ".do",
  ".log",
  ".vcf",
  ".bed",
  ".gff",
  ".gtf",
  ".fasta",
  ".fa",
  ".fastq",
  ".sam",
  ".pdb",
  ".cif",
  ".mmcif",
]);

// Classify an upload from its filename into the (filetype, artifact_type) pair
// the create endpoint requires. Text extensions stay previewable; everything
// else — the safe default — is a blob, which the sandbox still loads as a
// local file path.
export function classifyUpload(filename: string): {
  filetype: FileType;
  artifact_type: ArtifactType;
} {
  const dot = filename.lastIndexOf(".");
  const ext = dot >= 0 ? filename.slice(dot).toLowerCase() : "";
  if (TEXT_EXTENSIONS.has(ext)) {
    return { filetype: "text", artifact_type: "text" };
  }
  return { filetype: "blob", artifact_type: "blob" };
}

const artifactUploadUrlPath = (conversationId: string) =>
  `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/upload-url`;

const artifactFromUploadPath = (conversationId: string) =>
  `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/from-upload`;

// The presigned PUT is signed with this exact Content-Type (see
// generate_upload_url in the conversations service). S3 rejects the upload with
// SignatureDoesNotMatch if the browser sends anything else, so it is fixed here
// rather than derived from the File.
const UPLOAD_CONTENT_TYPE = "application/octet-stream";

// Largest file a user may attach. Kept in sync with `MAX_UPLOAD_BYTES` in
// product/ai/libs/conversations-types/src/conversations_types/uploads.py — the
// Python side is the source of truth and enforces it; this copy only buys the
// user an immediate error instead of a failed round trip.
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

// Shown when a file is refused for size before any request goes out — the only
// size-specific message a user sees, since a failed upload reports generically.
export const uploadTooLargeMessage = (filename: string, sizeBytes: number) =>
  `"${filename}" is ${(sizeBytes / 1024 / 1024).toFixed(1)} MB, which exceeds the ` +
  `${MAX_UPLOAD_BYTES / 1024 / 1024} MB upload limit.`;

// Upload one file to a conversation as a user attachment: fetch a presigned S3
// URL, PUT the bytes straight to S3, then mint the artifact record pointing at
// the stored object. The record is created only after the bytes land, so a
// failure before that step leaves nothing on the conversation to clean up.
// Returns the artifact id the user message links by; throws on any step failing.
export async function uploadFileToConversation(
  conversationId: string,
  file: File
): Promise<string> {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(uploadTooLargeMessage(file.name, file.size));
  }

  const { filetype, artifact_type } = classifyUpload(file.name);

  const presigned = await apiMutate<PresignedUpload>(
    artifactUploadUrlPath(conversationId),
    "POST",
    {
      filetype,
      artifact_type,
      filename: file.name,
      size_bytes: file.size,
    } satisfies ArtifactUploadUrlRequest,
    { onErrorMessage: `Couldn't start upload for "${file.name}".` }
  );

  const put = await fetch(presigned.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": UPLOAD_CONTENT_TYPE },
  });
  if (!put.ok) {
    throw new Error(`Upload to storage failed for "${file.name}" (${put.status}).`);
  }

  const created = await apiMutate<{ id: string }>(
    artifactFromUploadPath(conversationId),
    "POST",
    {
      filetype,
      artifact_type,
      filename: file.name,
      bucket: presigned.bucket,
      key: presigned.key,
    } satisfies ArtifactCreateFromUpload,
    { onErrorMessage: `Couldn't finish upload for "${file.name}".` }
  );

  return created.id;
}

// Upload several files to one conversation, resolving to their artifact ids in
// input order. Uploads run concurrently; `allSettled` lets every one finish
// before we act, so a fast failure can't roll back while a sibling is still
// writing. If any file fails, the records that were minted for the ones that
// succeeded are deleted before rethrowing — a partial batch never leaves
// artifacts that seed_artifacts would surface on a later turn.
export async function uploadFilesToConversation(
  conversationId: string,
  files: File[]
): Promise<string[]> {
  const results = await Promise.allSettled(
    files.map((file) => uploadFileToConversation(conversationId, file))
  );
  const failure = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
  if (failure) {
    await Promise.all(
      results
        .filter((r): r is PromiseFulfilledResult<string> => r.status === "fulfilled")
        .map((r) =>
          deleteArtifact(conversationId, r.value).catch((err) => {
            console.error("Failed to clean up an attachment after a batch upload failure", err);
          })
        )
    );
    throw failure.reason;
  }
  return results.map((r) => (r as PromiseFulfilledResult<string>).value);
}

// Soft-delete an artifact so it stops appearing in listings (and therefore in
// the agent's seeded artifacts).
export async function deleteArtifact(conversationId: string, artifactId: string): Promise<void> {
  await apiMutate<null>(artifactMetadataPath(conversationId, artifactId), "DELETE", undefined, {
    expects204: true,
  });
}
